import { Link, type LinkOptions } from '@tanstack/react-router'
import { useState, type ReactNode } from 'react'
import { cx } from './cx'

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

const sizes: Record<AvatarSize, string> = {
  xs: 'size-6 text-label',
  sm: 'size-8 text-label',
  md: 'size-10 text-small',
  lg: 'size-14 text-heading',
  xl: 'size-20 text-title',
}

// Neutral tints only: a coloured avatar would read as a status. Picked deterministically per name.
const tints = ['bg-ink/[0.05]', 'bg-ink/[0.08]', 'bg-ink/[0.11]', 'bg-ink/[0.065]', 'bg-ink/[0.095]']

function hash(s: string) {
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0
  return Math.abs(h)
}

export function initials(name: string) {
  const parts = name.trim().split(/\s+/).filter((p) => !/^\d+$/.test(p))
  const first = parts[0]?.[0] ?? ''
  const last = parts.length > 1 ? (parts.at(-1)?.[0] ?? '') : (parts[0]?.[1] ?? '')
  return (first + last).toUpperCase()
}

type AvatarProps = {
  name: string
  src?: string | null
  size?: AvatarSize
  /** Decorative when the name is printed next to it (default). */
  decorative?: boolean
}

export function Avatar({ name, src, size = 'md', decorative = true }: AvatarProps) {
  const [failed, setFailed] = useState(false)
  const a11y = decorative ? { 'aria-hidden': true as const } : { role: 'img' as const, 'aria-label': name }
  return (
    <span
      {...a11y}
      className={cx(
        'relative inline-grid shrink-0 place-items-center overflow-hidden rounded-full font-medium tracking-normal text-ink-2 select-none',
        sizes[size],
        tints[hash(name) % tints.length],
      )}
    >
      {/* Initials underneath: visible while the photo loads and if it fails. */}
      {initials(name)}
      {src && !failed && (
        <img
          src={src}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
          className="absolute inset-0 size-full object-cover"
        />
      )}
      {/* Hairline ring keeps light photos from bleeding into the page. */}
      <span className="pointer-events-none absolute inset-0 rounded-full ring-1 ring-ink/[0.06] ring-inset" />
    </span>
  )
}

type PersonChipProps = {
  name: string
  src?: string | null
  /** "ZM · #8" or "Trainer". */
  meta?: ReactNode
  size?: 'sm' | 'md'
  link?: LinkOptions
}

export function PersonChip({ name, src, meta, size = 'md', link }: PersonChipProps) {
  const content = (
    <>
      <Avatar name={name} src={src ?? null} size={size === 'sm' ? 'sm' : 'md'} />
      <span className="flex min-w-0 flex-col">
        <span className={cx('truncate font-medium text-ink', size === 'sm' ? 'text-small' : 'text-body')}>{name}</span>
        {meta && <span className="truncate text-small text-ink-3">{meta}</span>}
      </span>
    </>
  )
  const cls = cx('inline-flex min-w-0 items-center', size === 'sm' ? 'gap-2' : 'gap-3')
  if (!link) return <span className={cls}>{content}</span>
  return (
    <Link {...link} className={cx(cls, 'rounded-control hover:[&_span.font-medium]:underline hover:[&_span.font-medium]:underline-offset-2')}>
      {content}
    </Link>
  )
}

const logoSizes = { sm: 'size-6 text-label', md: 'size-8 text-label', lg: 'size-12 text-small', xl: 'size-16 text-body' }

/** Words that every club has; skipped for the monogram so "TSV Mariendorf 1897" becomes "MA", not "TS". */
const prefixes = new Set(['fc', 'sc', 'sv', 'tsv', 'tus', 'vfb', 'vfl', 'bsc', 'fsv', 'sg', 'ssv', 'tsg', 'spvgg', 'bfc', '1.', 'fk', 'ev', 'e.v.'])

function monogram(name: string) {
  const words = name.split(/\s+/).filter((w) => w && !/^\d+$/.test(w))
  const core = words.filter((w) => !prefixes.has(w.toLowerCase()))
  const pick = core.length ? core : words
  if (pick.length >= 2) return (pick[0]![0]! + pick[1]![0]!).toUpperCase()
  return (pick[0] ?? '?').slice(0, 2).toUpperCase()
}

type ClubLogoProps = { name: string; src?: string | null; size?: keyof typeof logoSizes; decorative?: boolean }

export function ClubLogo({ name, src, size = 'md', decorative = true }: ClubLogoProps) {
  const [failed, setFailed] = useState(false)
  const a11y = decorative ? { 'aria-hidden': true as const } : { role: 'img' as const, 'aria-label': name }
  return (
    <span
      {...a11y}
      className={cx('inline-grid shrink-0 place-items-center select-none', logoSizes[size])}
    >
      {src && !failed ? (
        <img src={src} alt="" loading="lazy" decoding="async" onError={() => setFailed(true)} className="size-full object-contain" />
      ) : (
        <span className="grid size-full place-items-center rounded-[28%] bg-bg-hover font-semibold tracking-normal text-ink-2">
          {monogram(name)}
        </span>
      )}
    </span>
  )
}
